import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { APPROVAL_LABEL, APPROVAL_TONE, DOES_NOT_PENCIL, FAMILY_LABEL, acres, approvalSentence, pct, phoneApproval, residualText, sizeSentence, usd } from '../lib/feasibilityLabels';
import { downloadCsv } from '../utils/csvDownload';
import UpsideMap from '../components/landUpside/UpsideMap';
import AssumptionsPanel from '../components/landUpside/AssumptionsPanel';
import type { Approval, BuildingFamily, FeasibilityCoverage, LandUpsideQuery, LandUpsideRow } from '../types/feasibility';

/**
 * FZ4 - the Land Upside page (spec section 10): every land property's stored answer, the plays first.
 * ⛔ It always says how much of the land is covered yet - a short list must never read as "no upside".
 */

const PAGE_SIZE = 50;
const APPROVAL_CHOICES: Approval[] = ['by_right', 'permit', 'unclear', 'not_allowed_now'];

export const coverageLine = (c: FeasibilityCoverage | null): string => {
  if (!c) return '';
  const cities = Object.entries(c.byJurisdiction).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  const head = `${c.scored.toLocaleString('en-US')} of ${c.landTotal.toLocaleString('en-US')} land properties scored`;
  return cities.length ? `${head} - ${cities.join(', ')}; the rest not researched yet.` : `${head} - no city researched yet.`;
};

const valueCell = (r: LandUpsideRow) => residualText(r);
const upsideCell = (r: LandUpsideRow) => (r.upside !== null ? `${usd(r.upside)} (${pct(r.upside_share)})` : (r.asking_note ?? '-'));
/**
 * The phone's one line (the 141st's look on the iPhone): where there is no number it says WHY - "Allowed outright · -"
 * read as a play on a lot too small to build on - and a building that does not pencil says so beside its upside.
 */
const phoneTail = (r: LandUpsideRow, tab: 'listed' | 'unlisted') => {
  if (tab === 'listed' && r.upside !== null) return `${upsideCell(r)}${(r.residual_value ?? 0) < 0 ? ` - ${DOES_NOT_PENCIL}` : ''}`;
  return `${valueCell(r)}${r.asking_note ? ` (${r.asking_note})` : ''}`;
};

export default function LandUpside() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState<'listed' | 'unlisted'>('listed');
  const [jurisdiction, setJurisdiction] = useState('');
  const [minAcres, setMinAcres] = useState('');
  const [family, setFamily] = useState<BuildingFamily | ''>('');
  const [approvals, setApprovals] = useState<Approval[]>(['by_right', 'permit']);
  const [minUpsidePct, setMinUpsidePct] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<LandUpsideRow[]>([]);
  const [total, setTotal] = useState(0);
  const [coverage, setCoverage] = useState<FeasibilityCoverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [view, setView] = useState<'list' | 'map'>('list');
  const [showDefaults, setShowDefaults] = useState(false);

  const query = useMemo((): LandUpsideQuery => ({
    tab, page, pageSize: PAGE_SIZE, approvals,
    ...(jurisdiction ? { jurisdiction } : {}),
    ...(minAcres ? { minAcres: Number(minAcres) } : {}),
    ...(family ? { family } : {}),
    ...(tab === 'listed' && minUpsidePct ? { minUpsideShare: Number(minUpsidePct) / 100 } : {}),
  }), [tab, page, approvals, jurisdiction, minAcres, family, minUpsidePct]);

  // ⛔ Number each load; an answer applies only if it is still the latest (the 141st's look: three quick filter changes
  // left the table on an older reply while the box showed the newer filter - each read takes 1-3 seconds).
  const latestLoad = useRef(0);
  const load = useCallback(async () => {
    const mine = ++latestLoad.current;
    setLoading(true);
    setError('');
    try {
      const r = await api.getLandUpside(query);
      if (mine !== latestLoad.current) return;
      setRows(r.rows);
      setTotal(r.total);
      setCoverage(r.coverage);
    } catch (e) {
      if (mine !== latestLoad.current) return;
      setError(e instanceof Error ? e.message : 'Could not load the land upside list');
    } finally {
      if (mine === latestLoad.current) setLoading(false);
    }
  }, [query]);

  useEffect(() => { void load(); }, [load]);

  const reset = <T,>(set: (v: T) => void) => (v: T) => { setPage(0); set(v); };
  const toggleApproval = (a: Approval) => {
    setPage(0);
    setApprovals((cur) => (cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]));
  };

  const exportCsv = async () => {
    setError('');
    try {
      const all: LandUpsideRow[] = [];
      for (let p = 0; p < 4; p++) {
        const r = await api.getLandUpside({ ...query, page: p, pageSize: 500 });
        all.push(...r.rows);
        if (r.rows.length < 500) break;
      }
      downloadCsv(
        `land-upside-${tab}-${new Date().toISOString().slice(0, 10)}.csv`,
        ['Address', 'City', 'Acres', 'Zone', 'Building', 'Approval', 'Biggest building (sq ft)', 'What a developer could pay', 'What similar land sold for', 'Asking', 'Upside', 'Upside %', 'Note'],
        all.map((r) => [
          r.master_properties?.address ?? '', r.master_properties?.city ?? '', r.land_sf ? (r.land_sf / 43_560).toFixed(2) : '',
          r.zone_code ?? '', FAMILY_LABEL[r.use_family], approvalSentence(r), r.max_building_sf, r.residual_value,
          r.land_comp_value, r.asking_price, r.upside, r.upside_share === null ? '' : Math.round(r.upside_share * 1000) / 10,
          r.residual_note ?? ((r.residual_value ?? 0) < 0 ? DOES_NOT_PENCIL : null) ?? r.asking_note ?? '',
        ]),
      );
      setNotice(`Exported ${all.length} ${all.length === 1 ? 'row' : 'rows'}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not export the list');
    }
  };

  const saveList = async () => {
    setError('');
    const ids = [...new Set(rows.map((r) => r.property_id))].slice(0, 200);
    const name = `Land Upside - ${tab === 'listed' ? 'listed' : 'not listed'} - ${new Date().toISOString().slice(0, 10)}`;
    try {
      await api.createProspectList({ name, description: coverageLine(coverage), filters: { property_ids: ids } });
      setNotice(`Saved ${ids.length} ${ids.length === 1 ? 'property' : 'properties'} to the prospect list "${name}".`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the prospect list');
    }
  };

  const recompute = async () => {
    setError('');
    try {
      const r = await api.recomputeFeasibility(jurisdiction || undefined);
      setNotice(`Recomputed ${r.summary.considered} properties.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not recompute');
    }
  };

  const cities = Object.keys(coverage?.byJurisdiction ?? {}).sort();
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      <div className="mx-auto max-w-7xl space-y-4 p-4">
        <div>
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] text-gray-900 sm:text-[32px]">Land Upside</h1>
          <p className="mt-1 text-sm text-gray-600">{coverageLine(coverage)}</p>
          <p className="text-xs text-gray-500">Estimates from each city's own documents and the app's own sales - confirm with Planning before quoting.</p>
        </div>

        <div className="flex gap-2" role="tablist">
          {(['listed', 'unlisted'] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t}
              className={`rounded-md px-3 py-1.5 text-sm ${tab === t ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => { setPage(0); setTab(t); }}>
              {t === 'listed' ? 'Listed for sale' : 'Not listed'}
            </button>
          ))}
        </div>

        <div data-row="filters" className="flex flex-wrap items-end gap-3 text-sm">
          <label className="flex flex-col">City
            <select className="rounded border px-2 py-1" value={jurisdiction} onChange={(e) => reset(setJurisdiction)(e.target.value)}>
              <option value="">All researched</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="flex flex-col">Acres, at least
            <input className="w-24 rounded border px-2 py-1" inputMode="decimal" value={minAcres} onChange={(e) => reset(setMinAcres)(e.target.value)} />
          </label>
          <label className="flex flex-col">Building
            <select className="rounded border px-2 py-1" value={family} onChange={(e) => reset(setFamily)(e.target.value as BuildingFamily | '')}>
              <option value="">The best of the two</option>
              <option value="industrial_building">Industrial building</option>
              <option value="retail_building">Retail building</option>
            </select>
          </label>
          {tab === 'listed' && (
            <label className="flex flex-col">Upside, at least (%)
              <input className="w-24 rounded border px-2 py-1" inputMode="decimal" value={minUpsidePct} onChange={(e) => reset(setMinUpsidePct)(e.target.value)} />
            </label>
          )}
          <fieldset className="flex flex-wrap gap-2">
            <legend className="sr-only">Approval</legend>
            {APPROVAL_CHOICES.map((a) => (
              <label key={a} className="flex items-center gap-1">
                <input type="checkbox" checked={approvals.includes(a)} onChange={() => toggleApproval(a)} />{APPROVAL_LABEL[a]}
              </label>
            ))}
          </fieldset>
        </div>

        <div data-row="actions" className="flex flex-wrap gap-2 text-sm">
          <button type="button" className="rounded border px-3 py-1.5" onClick={() => void exportCsv()}>Export CSV</button>
          <button type="button" className="rounded border px-3 py-1.5" onClick={() => void saveList()} disabled={rows.length === 0}>Save these to a prospect list</button>
          <button type="button" className="rounded border px-3 py-1.5" onClick={() => setView(view === 'list' ? 'map' : 'list')}>{view === 'list' ? 'Show the map' : 'Show the list'}</button>
          <button type="button" className="rounded border px-3 py-1.5" onClick={() => setShowDefaults(!showDefaults)}>{showDefaults ? 'Hide the defaults' : 'The defaults'}</button>
          {isAdmin && <button type="button" className="rounded border px-3 py-1.5" onClick={() => void recompute()}>Recompute</button>}
        </div>

        {error && <p role="alert" className="rounded bg-red-50 p-2 text-sm text-red-800">{error}</p>}
        {notice && <p role="status" className="rounded bg-emerald-50 p-2 text-sm text-emerald-800">{notice}</p>}
        {showDefaults && <AssumptionsPanel />}

        {loading ? <p className="text-sm text-gray-500">Loading...</p> : view === 'map' ? <UpsideMap rows={rows} /> : (
          <>
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="p-2">Property</th><th className="p-2">Acres</th><th className="p-2">Zone</th><th className="p-2">Best use</th>
                    <th className="p-2">Biggest building</th><th className="p-2">What a developer could pay</th>
                    <th className="p-2">What similar land sold for</th>{tab === 'listed' && <><th className="p-2">Asking</th><th className="p-2">Upside</th></>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.property_id}-${r.use_family}`} className="border-t align-top">
                      <td className="p-2"><Link className="text-blue-700 underline" to={`/data-hub/${r.property_id}`}>{r.master_properties?.address ?? 'No address'}</Link><div className="text-xs text-gray-500">{r.master_properties?.city}</div></td>
                      <td className="p-2">{acres(r.land_sf)}</td>
                      <td className="p-2">{r.zone_code ?? '-'}</td>
                      <td className="p-2"><span className={`rounded px-2 py-0.5 text-xs ring-1 ${APPROVAL_TONE[r.approval]}`}>{approvalSentence(r)}</span><div className="text-xs text-gray-500">{FAMILY_LABEL[r.use_family]}</div></td>
                      <td className="p-2 text-xs">{sizeSentence(r)}</td>
                      <td className="p-2">{valueCell(r)}</td>
                      <td className="p-2">{r.land_comp_value !== null ? usd(r.land_comp_value) : 'not enough land sales'}</td>
                      {tab === 'listed' && <><td className="p-2">{usd(r.asking_price)}</td><td className="p-2 font-semibold">{upsideCell(r)}{r.disagreement && <div className="text-xs font-normal text-amber-700">the two values disagree</div>}</td></>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="sm:hidden divide-y">
              {rows.map((r) => (
                <li key={`${r.property_id}-${r.use_family}`} className="py-2">
                  <Link className="block" to={`/data-hub/${r.property_id}`}>
                    <span className="font-medium text-blue-700 underline">{r.master_properties?.address ?? 'No address'}</span>
                    <span className="block text-xs text-gray-600">{`${acres(r.land_sf)} · ${r.zone_code ?? '-'} · ${phoneApproval(r)} · ${phoneTail(r, tab)}`}</span>
                  </Link>
                </li>
              ))}
            </ul>
            {rows.length === 0 && <p className="text-sm text-gray-600">Nothing matches these filters yet. {coverageLine(coverage)}</p>}
            <div className="flex items-center gap-2 text-sm">
              <button type="button" className="rounded border px-2 py-1" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
              <span>{`Page ${page + 1} of ${pages} (${total.toLocaleString('en-US')} ${total === 1 ? 'row' : 'rows'})`}</span>
              <button type="button" className="rounded border px-2 py-1" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
